/**
 * Pipeline Stage Badge Component
 *
 * Displays a pipeline stage with its icon and label.
 */

import { PipelineStageIcon } from './PipelineStageIcon';
import type { PipelineStage, StageStatus } from '../../types/pipeline';
import { STAGE_LABELS, STAGE_DESCRIPTIONS } from '../../types/pipeline';

interface PipelineStageBadgeProps {
  stage: PipelineStage;
  status: StageStatus;
  showLabel?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}

const SELECTED_BG_COLORS: Record<StageStatus, string> = {
  completed: 'bg-accent-green/10 border-accent-green/30',
  active: 'bg-accent-yellow/10 border-accent-yellow/30',
  pending: 'bg-bg-hover border-border',
  skipped: 'bg-accent-blue/10 border-accent-blue/30',
  failed: 'bg-accent-red/10 border-accent-red/30',
};

export function PipelineStageBadge({
  stage,
  status,
  showLabel = true,
  isSelected = false,
  onClick,
}: PipelineStageBadgeProps) {
  const label = STAGE_LABELS[stage];
  const description = STAGE_DESCRIPTIONS[stage];

  const baseClasses =
    'flex flex-col items-center gap-1 p-2 rounded-lg transition-all cursor-pointer';
  const selectedClasses = isSelected
    ? `border-2 ${SELECTED_BG_COLORS[status]}`
    : 'border-2 border-transparent hover:bg-bg-hover';

  return (
    <button
      type="button"
      className={`${baseClasses} ${selectedClasses}`}
      onClick={onClick}
      title={description}
      aria-label={`${label} - ${status}`}
    >
      <PipelineStageIcon status={status} size="md" />
      {showLabel && (
        <span className="text-xs font-medium text-text-muted whitespace-nowrap">
          {label}
        </span>
      )}
    </button>
  );
}
